<template>
  <DsPageContent>
    <InvestorRelationsEventForm
      v-if="isEventWithForm"
      :title="content.title"
      :form-id="parseInt(content.form.formId)"
      :description="content.form.description"
      :news="content.news"
      :news-title="content.newsTitle"
      :see-all-news-link="content.seeAllNewsLink"
      :form-success-labels="content.formSuccessLabels"
    />

    <InvestorRelationsEvent
      v-else
      :title="content.title"
      :eyebrow="content.eyebrow"
      :description="content.description"
      :related-documents="content.relatedDocuments"
      :image="content.image"
      :video="videoContent"
      :live-stream-video="content.liveStreamVideo"
      :show-livestream="isLiveStreamVisible"
      :show-video="isVideoVisible"
      :show-image="isImageVisible"
    />
    <!-- For debbuging purpose -->
    <div
      class="debug"
      style="display: none"
    >
      {{ { isLiveStreamVisible, isVideoVisible, isImageVisible, currentPageState } }}
    </div>
    <div
      class="debug"
      style="display: none"
    >
      {{ { ...pageStateDebug } }}
    </div>
  </DsPageContent>
</template>
<script setup>
import { DsPageContent } from 'awds'
import { queryPageInvestorRelationsEventsDetail } from '../../data-layer/queries.ts'
const { labels } = useInvestorsGlobalData()
const { getSlug } = usePageUtils()
const { getLocaleIso } = useLocales()
const { queryContentful } = useQueries()
const { mappers } = useInvestorsDataLayer()
const { dataPageMapper } = useDataMapper()
const { content, onBeforeAdyenData, onAfterAdyenData } = useInvestorsPageData()

const route = useRoute()
const { locale: localeRef } = useI18n()
const slug = getSlug(route.path) || 'investor-relations'
const locale = getLocaleIso(localeRef.value)

const SHOW_FORM = ref('show-form')
const SHOW_IMAGE = ref('show-image')
const SHOW_VIDEO = ref('show-video')
const SHOW_LIVESTREAM = ref('show-livestream')

await onBeforeAdyenData({
  withRoutes: true,
  globalDataContentType: 'PageInvestorRelationsEventsDetail'
})

const { data } = await useAsyncData('investor-relations-' + localeRef.value + route.params.slug, async () => {
  const investorEventsDetailQuery = queryContentful(
    {
      query: queryPageInvestorRelationsEventsDetail,
      variables: { slug, locale, preview: useRuntimeConfig().public.preview }
    },
    { log: false }
  )

  const [eventsData] = await Promise.all([investorEventsDetailQuery])

  if (!eventsData) {
    return {
      pageData: {}
    }
  }

  const entry = eventsData.page.items[0]
  const events = eventsData.news.items

  const pageData = dataPageMapper(
    {
      ...entry,
      ...{ events },
      ...{ labels: labels.value }
    },
    mappers
  )

  try {
    await handleLazyUrlsMapping(pageData)
  } catch (e) {
    console.error("[ERROR]: Couldn't handle lazy urls in Events Detail", e)
  }

  return {
    pageData
  }
})

const currentPageState = ref('')
const pageStateDebug = ref({})

const isEventWithForm = computed(() => {
  return currentPageState.value === SHOW_FORM.value
})

const isLiveStreamVisible = computed(() => {
  return currentPageState.value === SHOW_LIVESTREAM.value
})

const isVideoVisible = computed(() => {
  return currentPageState.value === SHOW_VIDEO.value
})

const isImageVisible = computed(() => {
  return currentPageState.value === SHOW_IMAGE.value
})

const videoContent = computed(() => {
  return {
    ...content.value.video,
    ariaLabelClose: labels.value.close,
    ariaLabelPlay: labels.value.play
  }
})

/**
 * Establishes which page template and page element to be shown
 *
 * Note: Keep Knowledge Base up to date: https://youtrack.is.adyen.com/articles/WEB-A-174/Investors-Publications
 * Logic:
 * - Case 1: If Event dates (start and end) are in the future:
 *   - Event is `upcoming` -> form or banner image/video can be displayed
 *   - Case 1.a: Form will be displayed: If Form exists and is current, meanting that Form start date is in the past and the Form end date in future
 *   - Case 1.b: Banner image/video will be displayed: If Form does not exist or Form dates are not current
 * - Case 2: If Event dates (start and end) are in the present:
 *   - Event is `current` -> LiveStream or banner image/video can be displayed
 *   - Case 2.a: LiveStream will be displayed: If LiveStream exists and is current, meanting that the LiveStream start date is in the past and the LiveStream end date in future
 *   - Case 2.b: Banner image/video will be displayed: If LiveStream does not exist or LiveStream dates are not current
 * - Case 3: If Event dates (start and end) are in the past:
 *   - Event is `past` -> Banner image/video will be displayed
 *
 * @return {String} empty  or a state constant oneOf: ['show-form', 'show-image', 'show-video', 'show-livestream']
 */
const getCurrentPageState = () => {
  const hasImage = !!content.value.image
  const hasVideo = !!content.value.video
  const hasLiveStream = !!content.value.liveStreamVideo
  const hasForm = !!content.value.form
  const pageTimeState = eventTimeState(content.value?.startDate, content.value?.endDate)
  const formTimeState = eventTimeState(new Date(), content.value?.form?.endDate)
  const liveStreamData = content.value?.liveStreamVideo
  const liveStreamTimeState = eventTimeState(liveStreamData?.startDate, liveStreamData?.endDate)

  const videoOrImage = hasVideo ? SHOW_VIDEO : hasImage ? SHOW_IMAGE : ''

  // Set values to be render hidden in the page HTML for debugging purposes
  pageStateDebug.value = {
    pageTimeState,
    liveStreamTimeState,
    formTimeState,
    hasImage,
    hasVideo,
    hasLiveStream
  }

  // NOTE: to see the form set event date to tomorrow and form date to today
  if (pageTimeState?.upcoming) {
    return hasForm && formTimeState.current ? SHOW_FORM : videoOrImage
  }
  // NOTE: to see a LiveStream set the form date to today
  if (pageTimeState?.current) {
    return hasLiveStream && liveStreamTimeState.current ? SHOW_LIVESTREAM : videoOrImage
  }

  if (pageTimeState?.past) {
    return videoOrImage
  }
  return ''
}

onMounted(() => {
  currentPageState.value = getCurrentPageState().value
})

await onAfterAdyenData(data.value)
</script>
